/** Clinical age helpers — reject placeholder / corrupt birth dates and absurd ages. */

export const MAX_REASONABLE_AGE_YEARS = 120;

/**
 * Parse a birth date from API/DICOM-ish strings.
 * Rejects empty sentinels (0000-00-00, 1900-01-01 as pure placeholder is NOT auto-rejected
 * unless the resulting age is out of range).
 */
export function parseBirthDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // Common invalid / empty DICOM values
  if (/^(0+|0000-00-00|00000000)$/i.test(s)) return null;

  let d: Date | null = null;

  // YYYY-MM-DD or YYYY/MM/DD
  const iso = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const day = Number(iso[3]);
    d = new Date(Date.UTC(y, m - 1, day));
    if (d.getUTCFullYear() !== y || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== day) {
      return null;
    }
  } else if (/^\d{8}$/.test(s)) {
    // YYYYMMDD
    const y = Number(s.slice(0, 4));
    const m = Number(s.slice(4, 6));
    const day = Number(s.slice(6, 8));
    d = new Date(Date.UTC(y, m - 1, day));
    if (d.getUTCFullYear() !== y || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== day) {
      return null;
    }
  } else {
    const parsed = new Date(s);
    if (Number.isNaN(parsed.getTime())) return null;
    d = new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
  }

  return d;
}

export function ageYearsFromBirthDate(birth: Date, reference = new Date()): number | null {
  const ref = new Date(
    Date.UTC(reference.getFullYear(), reference.getMonth(), reference.getDate()),
  );
  const b = new Date(Date.UTC(birth.getUTCFullYear(), birth.getUTCMonth(), birth.getUTCDate()));

  if (b.getTime() > ref.getTime()) return null;

  let years = ref.getUTCFullYear() - b.getUTCFullYear();
  const monthDiff = ref.getUTCMonth() - b.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getUTCDate() < b.getUTCDate())) {
    years -= 1;
  }

  if (years < 0 || years > MAX_REASONABLE_AGE_YEARS) return null;
  return years;
}

/** Extract years from PACS/DICOM age strings like "062Y", "62A", "45", "4M" (months → years floor). */
export function ageYearsFromRaw(raw?: string | null): number | null {
  if (!raw) return null;
  const s = String(raw).trim().toUpperCase();
  if (!s) return null;

  // Explicit unit
  const withUnit = s.match(/^(\d+)\s*([YAMD])$/);
  if (withUnit) {
    const n = Number(withUnit[1]);
    const unit = withUnit[2];
    if (!Number.isFinite(n) || n < 0) return null;
    if (unit === 'Y' || unit === 'A') {
      return n <= MAX_REASONABLE_AGE_YEARS ? n : null;
    }
    if (unit === 'M') {
      const years = Math.floor(n / 12);
      return years <= MAX_REASONABLE_AGE_YEARS ? years : null;
    }
    // Days → 0 years for display as years (still valid infant)
    return 0;
  }

  // "062Y" padded DICOM AS style without separator already covered; also "62 años"
  const digits = s.match(/(\d{1,3})/);
  if (!digits) return null;
  const n = Number(digits[1]);
  if (!Number.isFinite(n) || n < 0 || n > MAX_REASONABLE_AGE_YEARS) return null;
  return n;
}

/**
 * Prefer a coherent birth date; fall back to raw age string.
 * Returns null when age cannot be trusted.
 */
export function resolvePatientAgeYears(
  rawAge?: string | null,
  birthDate?: string | null,
  reference = new Date(),
): number | null {
  const fromBirth = (() => {
    const d = parseBirthDate(birthDate);
    if (!d) return null;
    return ageYearsFromBirthDate(d, reference);
  })();

  if (fromBirth !== null) return fromBirth;

  return ageYearsFromRaw(rawAge);
}

/** UI label: "42 a" / "No definida" */
export function formatPatientAgeShort(age: number | null | undefined): string {
  if (age === null || age === undefined) return 'No definida';
  return `${age} a`;
}

/** UI label: "42 años" / "No definida" */
export function formatPatientAgeLong(age: number | null | undefined): string {
  if (age === null || age === undefined) return 'No definida';
  return `${age} ${age === 1 ? 'año' : 'años'}`;
}
